import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  Transform as TransformIcon,
  Timeline as TimelineIcon,
  LocationOn as LocationIcon,
  TrackChanges as TargetIcon,
  Build as BuildIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  Gavel as GavelIcon,
  Business as BusinessIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import caseStudyService, { CaseStudy, CaseStudySection } from '../services/caseStudyService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`case-study-tabpanel-${index}`}
      aria-labelledby={`case-study-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CaseStudyMAF: React.FC = () => {
  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editingSection, setEditingSection] = useState<string | null>(null);

  useEffect(() => {
    loadCaseStudy();
  }, []);

  const loadCaseStudy = async () => {
    try {
      setLoading(true);
      const data = await caseStudyService.getCaseStudy('maf-analytics-transformation');
      setCaseStudy(data);
      setError(null);
    } catch (err) {
      console.error('Error loading case study:', err);
      setError('Failed to load case study. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSectionClick = (sectionId: string) => {
    setEditingSection(sectionId);
    // In a full implementation, this would open an edit dialog
    console.log('Edit section:', sectionId);
  };

  const getIconForSection = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactElement } = {
      PeopleIcon: <PeopleIcon />,
      TransformIcon: <TransformIcon />,
      TimelineIcon: <TimelineIcon />,
      LocationIcon: <LocationIcon />,
      TargetIcon: <TargetIcon />,
      BuildIcon: <BuildIcon />,
      TrendingUpIcon: <TrendingUpIcon />
    };
    return iconMap[iconName] || <AssessmentIcon />;
  };

  const getCategoryColor = (category: string) => {
    const colorMap: { [key: string]: string } = {
      Analytics: '#1976d2',
      Governance: '#388e3c',
      Leadership: '#f57c00',
      Innovation: '#7b1fa2',
      Strategy: '#c62828'
    };
    return colorMap[category] || '#757575';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !caseStudy) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Case study not found'}</Alert>
        <Button onClick={loadCaseStudy} sx={{ mt: 2 }}>Retry</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #003366 0%, #005a9c 100%)' }}>
        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
          {caseStudy.title}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item>
            <Chip label={caseStudy.organization} sx={{ bgcolor: '#fff', fontWeight: 'bold' }} />
          </Grid>
          <Grid item>
            <Chip label={caseStudy.industry} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
          </Grid>
          <Grid item>
            <Chip label={caseStudy.region} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
          </Grid>
          <Grid item>
            <Chip label={`Status: ${caseStudy.status}`} sx={{ bgcolor: '#4caf50', color: '#fff' }} />
          </Grid>
        </Grid>
        <Typography variant="body1" sx={{ color: '#fff', lineHeight: 1.8 }}>
          {caseStudy.overview}
        </Typography>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="case study tabs">
          <Tab label="5W1H Framework" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Key Stakeholders" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Outcomes & Metrics" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Capstone Learning" icon={<SchoolIcon />} iconPosition="start" />
          <Tab label="DHS Alignment" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="References" icon={<LinkIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab 1: 5W1H Framework */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {caseStudy.sections
            .sort((a, b) => a.order - b.order)
            .map((section) => (
              <Grid item xs={12} key={section.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      borderColor: '#003366',
                      borderWidth: 2,
                      borderStyle: 'solid'
                    }
                  }}
                  onClick={() => handleSectionClick(section.id)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit ${section.title}`}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            bgcolor: '#003366',
                            color: '#fff',
                            borderRadius: '50%',
                            p: 1,
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getIconForSection(section.icon)}
                        </Box>
                        <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold' }}>
                          {section.title}
                        </Typography>
                      </Box>
                      <Tooltip title="Click anywhere to edit">
                        <IconButton size="small" sx={{ color: '#003366' }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <List>
                      {section.content.map((item, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              variant: 'body1',
                              sx: { lineHeight: 1.7 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </TabPanel>

      {/* Tab 2: Key Stakeholders */}
      <TabPanel value={tabValue} index={1}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: 6,
              borderColor: '#003366',
              borderWidth: 2,
              borderStyle: 'solid'
            }
          }}
          onClick={() => handleSectionClick('stakeholders')}
          role="button"
          tabIndex={0}
          aria-label="Edit stakeholders"
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold' }}>
                Key Stakeholders & Leadership
              </Typography>
              <Tooltip title="Click anywhere to edit">
                <IconButton size="small" sx={{ color: '#003366' }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={3}>
              {caseStudy.stakeholders.map((stakeholder, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Paper elevation={2} sx={{ p: 3, height: '100%', bgcolor: '#f5f5f5' }}>
                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 1 }}>
                      {stakeholder.name}
                    </Typography>
                    <Chip label={stakeholder.role} size="small" sx={{ mb: 2, bgcolor: '#003366', color: '#fff' }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                      {stakeholder.contribution}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 3: Outcomes & Metrics */}
      <TabPanel value={tabValue} index={2}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: 6,
              borderColor: '#003366',
              borderWidth: 2,
              borderStyle: 'solid'
            }
          }}
          onClick={() => handleSectionClick('outcomes')}
          role="button"
          tabIndex={0}
          aria-label="Edit outcomes"
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold' }}>
                Outcomes & Performance Metrics
              </Typography>
              <Tooltip title="Click anywhere to edit">
                <IconButton size="small" sx={{ color: '#003366' }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={2}>
              {caseStudy.outcomes.map((outcome, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Chip
                        label={outcome.category}
                        size="small"
                        sx={{ bgcolor: '#003366', color: '#fff', mr: 1 }}
                      />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#003366' }}>
                        {outcome.metric}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {outcome.baseline && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Baseline:</strong> {outcome.baseline}
                      </Typography>
                    )}
                    {outcome.target && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Target:</strong> {outcome.target}
                      </Typography>
                    )}
                    {outcome.achieved && (
                      <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                        <strong>Achieved:</strong> {outcome.achieved}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 4: Capstone Learning */}
      <TabPanel value={tabValue} index={3}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: 6,
              borderColor: '#003366',
              borderWidth: 2,
              borderStyle: 'solid'
            }
          }}
          onClick={() => handleSectionClick('capstone')}
          role="button"
          tabIndex={0}
          aria-label="Edit capstone objectives"
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold' }}>
                UC Berkeley Expand Capstone Learning Objectives
              </Typography>
              <Tooltip title="Click anywhere to edit">
                <IconButton size="small" sx={{ color: '#003366' }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={3}>
              {caseStudy.capstoneObjectives.map((objective) => (
                <Grid item xs={12} key={objective.id}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" width="100%">
                        <Chip
                          label={objective.category}
                          size="small"
                          sx={{
                            bgcolor: getCategoryColor(objective.category),
                            color: '#fff',
                            mr: 2,
                            fontWeight: 'bold'
                          }}
                        />
                        <Typography variant="h6" sx={{ color: '#003366' }}>
                          {objective.objective}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ p: 2 }}>
                        {/* DHS Alignment */}
                        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#003366', mb: 1 }}>
                            <SecurityIcon sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle' }} />
                            DHS Government Alignment
                          </Typography>
                          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            {objective.dhsAlignment}
                          </Typography>
                        </Paper>

                        {/* Activities */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#003366', mb: 1 }}>
                          Student Activities:
                        </Typography>
                        <List dense>
                          {objective.activities.map((activity, idx) => (
                            <ListItem key={idx}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircleIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary={activity} />
                            </ListItem>
                          ))}
                        </List>

                        {/* Outcomes */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#003366', mb: 1, mt: 2 }}>
                          Expected Outcomes:
                        </Typography>
                        <List dense>
                          {objective.outcomes.map((outcome, idx) => (
                            <ListItem key={idx}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary={outcome} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 5: DHS Alignment */}
      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          {/* Mission Alignment */}
          {caseStudy.dhsAlignment.missionAlignment && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    DHS Mission Alignment
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    {caseStudy.dhsAlignment.missionAlignment}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Analytics Maturity Roadmap */}
          {caseStudy.dhsAlignment.analyticsMaturityRoadmap && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    DHS Analytics Maturity Roadmap
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                    {caseStudy.dhsAlignment.analyticsMaturityRoadmap.overview}
                  </Typography>

                  <Grid container spacing={2}>
                    {caseStudy.dhsAlignment.analyticsMaturityRoadmap.stages.map((stage, idx) => (
                      <Grid item xs={12} md={6} key={idx}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: '#e3f2fd' }}>
                          <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 1 }}>
                            {stage.stage}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                            {stage.description}
                          </Typography>
                          <List dense>
                            {stage.activities.map((activity, actIdx) => (
                              <ListItem key={actIdx} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <CheckCircleIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={activity}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Paper elevation={1} sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Key Principles:
                    </Typography>
                    <List dense>
                      {caseStudy.dhsAlignment.analyticsMaturityRoadmap.keyPrinciples.map((principle, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={principle} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Governance Framework */}
          {caseStudy.dhsAlignment.governanceFramework && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Governance Framework for Sensitive Data
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                    {caseStudy.dhsAlignment.governanceFramework.overview}
                  </Typography>

                  <Grid container spacing={2}>
                    {caseStudy.dhsAlignment.governanceFramework.pillars.map((pillar, idx) => (
                      <Grid item xs={12} md={6} key={idx}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: '#f3e5f5' }}>
                          <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 1 }}>
                            {pillar.pillar}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                            {pillar.description}
                          </Typography>
                          <List dense>
                            {pillar.components.map((component, compIdx) => (
                              <ListItem key={compIdx} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <CheckCircleIcon sx={{ color: '#7b1fa2', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={component}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Pilot Program Approach */}
          {caseStudy.dhsAlignment.pilotProgramApproach && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    DHS Pilot Program: Test-and-Learn Approach
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                    {caseStudy.dhsAlignment.pilotProgramApproach.overview}
                  </Typography>

                  {/* Current Pilots */}
                  <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#e8f5e9' }}>
                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                      Current DHS Pilot Programs:
                    </Typography>
                    <List dense>
                      {caseStudy.dhsAlignment.pilotProgramApproach.currentPilots.map((pilot, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={pilot} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>

                  {/* Test and Learn Strategies */}
                  <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    Test-and-Learn Strategies:
                  </Typography>
                  <Grid container spacing={2}>
                    {caseStudy.dhsAlignment.pilotProgramApproach.testAndLearnStrategies.map((strategy, idx) => (
                      <Grid item xs={12} key={idx}>
                        <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff3e0' }}>
                          <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 1 }}>
                            {strategy.strategy}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                            {strategy.description}
                          </Typography>
                          <List dense>
                            {strategy.implementation.map((item, itemIdx) => (
                              <ListItem key={itemIdx} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <CheckCircleIcon sx={{ color: '#f57c00', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Success Factors */}
                  <Paper elevation={1} sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Success Factors:
                    </Typography>
                    <List dense>
                      {caseStudy.dhsAlignment.pilotProgramApproach.successFactors.map((factor, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={factor} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Security Considerations & Compliance */}
          <Grid item xs={12} md={6}>
            {caseStudy.dhsAlignment.securityConsiderations && (
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Security Considerations
                  </Typography>
                  <List dense>
                    {caseStudy.dhsAlignment.securityConsiderations.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon sx={{ color: '#f57c00', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {caseStudy.dhsAlignment.complianceFrameworks && (
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Compliance Frameworks
                  </Typography>
                  <List dense>
                    {caseStudy.dhsAlignment.complianceFrameworks.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Government Applications */}
          {caseStudy.dhsAlignment.governmentApplications && (
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                    <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Government Applications
                  </Typography>
                  <List dense>
                    {caseStudy.dhsAlignment.governmentApplications.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon sx={{ color: '#0288d1', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Tab 6: References */}
      <TabPanel value={tabValue} index={5}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: 6,
              borderColor: '#003366',
              borderWidth: 2,
              borderStyle: 'solid'
            }
          }}
          onClick={() => handleSectionClick('references')}
          role="button"
          tabIndex={0}
          aria-label="Edit references"
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h5" sx={{ color: '#003366', fontWeight: 'bold' }}>
                References & Resources
              </Typography>
              <Tooltip title="Click anywhere to edit">
                <IconButton size="small" sx={{ color: '#003366' }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={2}>
              {caseStudy.references.map((reference, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      bgcolor: '#f5f5f5',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: '#e3f2fd',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="flex-start">
                      <LinkIcon sx={{ color: '#003366', mr: 1, mt: 0.5 }} />
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Chip label={reference.type} size="small" sx={{ bgcolor: '#003366', color: '#fff', mr: 1 }} />
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {reference.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="a"
                          href={reference.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#1976d2',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            },
                            wordBreak: 'break-all'
                          }}
                        >
                          {reference.url}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>
    </Container>
  );
};

export default CaseStudyMAF;
